import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import type { ColumnDef } from "@tanstack/react-table";
import { Layers2 } from "lucide-react";
import type { Secret } from "@/app/secrets/types";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { getBindingInfo } from "@/lib/binding";
import { getRelativeTimeString } from "@/lib/dates";
import { SecretActions } from "./secret-actions";

type CreateSecretColumnsOptions = {
  overriddenSecrets?: Set<string>;
  overridingSecrets?: Set<string>;
  // Edit/delete are the only row actions, both gated per-row. Without either
  // permission the column is non-actionable for every row — drop it entirely
  // rather than render a blank column.
  showActions?: boolean;
};

export const createSecretColumns = ({
  overriddenSecrets,
  overridingSecrets,
  showActions = true,
}: CreateSecretColumnsOptions = {}) => {
  const columns: ColumnDef<Secret>[] = [
    {
      id: "id",
      accessorKey: "id",
      enableHiding: true,
      enableSorting: false,
      meta: {
        displayName: "ID",
        defaultVisible: false,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="ID" />
      ),
      cell: ({ row }) => <span>{row.original.id}</span>,
    },
    {
      id: "key",
      accessorKey: "key",
      meta: {
        displayName: "Key",
        defaultVisible: true,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Key" />
      ),
      cell: ({ row }) => {
        const isOverridden = overriddenSecrets?.has(row.original.key);
        const isOverriding = overridingSecrets?.has(row.original.key);

        return (
          <div className="flex items-center gap-2">
            {!!isOverridden && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Layers2 className="h-3.5 w-3.5 text-orange-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Overridden by a project secret</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {!!isOverriding && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Layers2 className="h-3.5 w-3.5 text-green-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Overrides organization or global secret</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <span
              className={
                isOverridden ? "text-muted-foreground line-through" : ""
              }
            >
              {row.original.key}
            </span>
          </div>
        );
      },
    },
    {
      id: "binding_type",
      accessorKey: "binding.type",
      meta: {
        displayName: "Type",
        defaultVisible: true,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Type" />
      ),
      cell: ({ row }) => {
        const bindingType = row.original.binding.type;
        const isOverridden = overriddenSecrets?.has(row.original.key);
        const bindingInfo = getBindingInfo(bindingType);
        const { background, hoverBackground, ring, badgeText, badgeBorder } =
          bindingInfo.classes;

        let className = `${background} ${hoverBackground} ${ring} ${badgeText} ${badgeBorder}`;

        if (isOverridden) {
          className += " opacity-60";
        }

        return (
          <Badge className={className} variant="secondary">
            {bindingInfo.displayName}
          </Badge>
        );
      },
    },
    {
      id: "description",
      accessorKey: "description",
      meta: {
        displayName: "Description",
        defaultVisible: true,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Description" />
      ),
      cell: ({ row }) => {
        const isOverridden = overriddenSecrets?.has(row.original.key);
        return (
          <span className={isOverridden ? "opacity-60" : ""}>
            {row.original.description || "No description"}
          </span>
        );
      },
    },
    {
      id: "last_accessed_at",
      accessorKey: "last_accessed_at",
      meta: {
        displayName: "Last Accessed",
        defaultVisible: true,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Last Accessed" />
      ),
      cell: ({ row }) => {
        const isOverridden = overriddenSecrets?.has(row.original.key);

        if (!row.original.last_accessed_at) {
          return (
            <span className={isOverridden ? "opacity-60" : ""}>Never</span>
          );
        }

        const lastAccessedAt = new Date(row.original.last_accessed_at);
        return (
          <time
            className={isOverridden ? "opacity-60" : ""}
            dateTime={lastAccessedAt.toISOString()}
            title={lastAccessedAt.toUTCString()}
          >
            {getRelativeTimeString(lastAccessedAt)}
          </time>
        );
      },
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      meta: {
        displayName: "Created At",
        defaultVisible: true,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created At" />
      ),
      cell: ({ row }) => {
        const isOverridden = overriddenSecrets?.has(row.original.key);

        if (!row.original.created_at) {
          return <span className={isOverridden ? "opacity-60" : ""}>-</span>;
        }

        const createdAt = new Date(row.original.created_at);
        return (
          <time
            className={isOverridden ? "opacity-60" : ""}
            dateTime={createdAt.toISOString()}
            title={createdAt.toUTCString()}
          >
            {getRelativeTimeString(createdAt)}
          </time>
        );
      },
    },

    {
      id: "updated_at",
      accessorKey: "updated_at",
      enableHiding: true,
      meta: {
        displayName: "Updated At",
        defaultVisible: false,
      },
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Updated At" />
      ),
      cell: ({ row }) => {
        const isOverridden = overriddenSecrets?.has(row.original.key);

        if (!row.original.updated_at) {
          return <span className={isOverridden ? "opacity-60" : ""}>-</span>;
        }

        const updatedAt = new Date(row.original.updated_at);
        return (
          <time
            className={isOverridden ? "opacity-60" : ""}
            dateTime={updatedAt.toISOString()}
            title={updatedAt.toUTCString()}
          >
            {getRelativeTimeString(updatedAt)}
          </time>
        );
      },
    },
  ];

  if (showActions) {
    columns.push({
      id: "actions",
      enableHiding: false,
      meta: {
        displayName: "Actions",
        defaultVisible: true,
      },
      header: () => <div className="flex items-center space-x-2">Actions</div>,
      cell: ({ row }) => <SecretActions secret={row.original} />,
    });
  }

  return columns;
};
